import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, ConfigProvider, Drawer, Input, Table, Tooltip } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { SorterResult } from "antd/es/table/interface";
import { useApolloClient, useQuery } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import { GET_EQUIPMENTS } from "graphql/query/equipment-gql";
import { GET_EXHIBITIONS_CURSOR } from "graphql/query/exhibition-gql";
import { GET_FLOORING_CURSOR } from "graphql/query/flooring-gql";
import { GET_ORGANIZERS_CURSOR } from "graphql/query/organizer-gql";
import Communication from "components/communication";
import customizeTheme from "components/customize-theme";
import type {
  TCommunicationAuditEntry,
  TCommunicationDismissReason,
  TCommunicationFilters,
  TCommunicationReferenceOption,
  TCommunicationTab,
  TCommunicationTableLayout,
  TCommunicationTask,
  TCommunicationTaskFormValues,
} from "components/communication/helpers/communication-types-helper";
import Icons from "components/icons";
import { PageWrapDefault } from "components/layout";
import { DrawerStandart, Localize } from "components/service";
import { localizeByKey, localizeByKeyStatic } from "components/service/localize";
import { useActiveMenuItem, useBreadCrumbs, useMe, useWindowSize } from "components/use-hooks";

import "./communication.scss";

const { Search } = Input;
const basePath = Communication.Const.basePath;
const EQUIPMENT_PAGE_SIZE = 100;
const emptyFilters: TCommunicationFilters = {
  event_name: "",
  sender: "",
  company_name: "",
  stand_number: "",
};

type TCommunicationSortState = {
  key?: string;
  order?: "ascend" | "descend";
};

const TABLE_LAYOUT_BREAKPOINTS = {
  compact: 1160,
  tablet: 920,
};
const PREVIEW_RAIL_BREAKPOINT = 1199;
const DRAWER_WIDTH_BREAKPOINTS = {
  mobile: 767,
  tablet: 1199,
  laptop: 1519,
};

const getActiveFilterCount = (filters: TCommunicationFilters): number =>
  Object.values(filters).filter((value) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return Boolean(value);
  }).length;

const getTabFromPath = (pathname: string): TCommunicationTab => {
  if (pathname.startsWith(`${basePath}/audit`)) {
    return "audit";
  }

  if (pathname.startsWith(`${basePath}/unidentified`)) {
    return "unidentified";
  }

  if (pathname.startsWith(`${basePath}/pending`)) {
    return "pending";
  }

  return "inbox";
};

const getPageFromPath = (pathname: string): number => {
  const pageMatch = pathname.match(/\/page\/(\d+)/);
  return pageMatch ? parseInt(pageMatch[1], 10) : 1;
};

const getRouteByTab = (tab: TCommunicationTab, page = 1): string => {
  const tabPath =
    tab === "audit"
      ? `${basePath}/audit`
      : tab === "unidentified"
        ? `${basePath}/unidentified`
        : tab === "pending"
          ? `${basePath}/pending`
          : basePath;

  return page > 1 ? `${tabPath}/page/${page}` : tabPath;
};

const mapCursorOptions = (
  edges: Array<{ node: { id: string; title: string } }> | undefined
): TCommunicationReferenceOption[] =>
  edges?.map(({ node }) => ({
    value: node.id,
    label: node.title,
  })) || [];

const sortReferenceOptions = (
  options: TCommunicationReferenceOption[]
): TCommunicationReferenceOption[] =>
  [ ...options ].sort((left, right) => left.label.localeCompare(right.label));

const mapEquipmentOptions = (
  equipments: Array<{ id: string; group_id: string; title: string }> | undefined
): TCommunicationReferenceOption[] =>
  equipments?.map((equipment) => ({
    value: equipment.group_id,
    label: equipment.title,
    rawId: equipment.id,
  })) || [];

const preventNavigation = (event: React.MouseEvent<HTMLElement>) => {
  event.preventDefault();
  event.stopPropagation();
};

const renderTabLabel = ({
  label,
  tooltip,
  count,
}: {
  label: string;
  tooltip: string;
  count?: number;
}) => (
  <span className="communication-tab-label">
    <span className="communication-tab-label__text">{label}</span>
    {typeof count === "number" && (
      <span className="communication-tab-label__badge">{count}</span>
    )}
    <Tooltip title={tooltip}>
      <span
        className="communication-tab-label__info"
        onClick={preventNavigation}
      >
        <Icons.Information />
      </span>
    </Tooltip>
  </span>
);

const matchesSearch = (task: TCommunicationTask, searchText: string): boolean => {
  const lowerSearch = searchText.toLowerCase().trim();

  if (!lowerSearch) {
    return true;
  }

  return [
    task.sender,
    task.subject,
    task.event_name,
    task.company_name,
    task.stand_number,
    task.ai_summary,
  ]
    .filter(Boolean)
    .some((value) => value.toLowerCase().includes(lowerSearch));
};

const compareStrings = (left: string | null | undefined, right: string | null | undefined): number =>
  (left || "").localeCompare(right || "");

const compareDates = (left: string | null | undefined, right: string | null | undefined): number =>
  new Date(left || 0).getTime() - new Date(right || 0).getTime();

const getTableLayoutMode = (width: number): TCommunicationTableLayout => {
  if (width > 0 && width < TABLE_LAYOUT_BREAKPOINTS.tablet) {
    return "tablet";
  }

  if (width > 0 && width < TABLE_LAYOUT_BREAKPOINTS.compact) {
    return "compact";
  }

  return "full";
};

const getTableScrollWidth = (layoutMode: TCommunicationTableLayout): number => {
  if (layoutMode === "tablet") {
    return 960;
  }

  if (layoutMode === "compact") {
    return 1020;
  }

  return 1320;
};

const getCommunicationDrawerWidth = (viewportWidth: number): number => {
  if (viewportWidth > 0 && viewportWidth <= DRAWER_WIDTH_BREAKPOINTS.mobile) {
    return Math.max(360, viewportWidth - 12);
  }

  if (viewportWidth > 0 && viewportWidth <= DRAWER_WIDTH_BREAKPOINTS.tablet) {
    return Math.max(720, viewportWidth - 24);
  }

  if (viewportWidth > 0 && viewportWidth <= DRAWER_WIDTH_BREAKPOINTS.laptop) {
    return Math.min(1180, viewportWidth - 40);
  }

  if (viewportWidth > 0) {
    return Math.min(1240, viewportWidth - 56);
  }

  return 980;
};

const getDrawerTitleKey = (
  currentTab: TCommunicationTab,
  task?: TCommunicationTask | null
): string =>
  currentTab === "audit"
    || task?.status === Communication.Const.statuses.APPROVED
    || task?.status === Communication.Const.statuses.DISMISSED
    ? "COMMUNICATION.Title_AuditEntry"
    : "COMMUNICATION.Title_EmailChange";

const isInteractiveRowTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return Boolean(
    target.closest(
      "a, button, input, textarea, select, [role='button'], .ant-popover, .ant-popover-content, .ant-tooltip, .communication-original-email"
    )
  );
};

const ROW_HINT_OFFSET_X = 16;
const ROW_HINT_OFFSET_Y = 18;
const ROW_HINT_WIDTH = 220;
const ROW_HINT_HEIGHT = 34;
const ROW_HINT_DELAY_MS = 460;
const ROW_HINT_HIDE_MS = 3000;

const sortCommunicationTasks = (
  tasks: TCommunicationTask[],
  sortState: TCommunicationSortState
): TCommunicationTask[] => {
  if (!sortState.key || !sortState.order) {
    return tasks;
  }

  const sortedTasks = [ ...tasks ];
  const direction = sortState.order === "ascend" ? 1 : -1;

  sortedTasks.sort((left, right) => {
    const leftIsDemo = Boolean(left.demo_badge_key);
    const rightIsDemo = Boolean(right.demo_badge_key);

    if (leftIsDemo !== rightIsDemo) {
      return leftIsDemo ? -1 : 1;
    }

    let compareResult = 0;

    switch (sortState.key) {
      case "sender":
        compareResult = compareStrings(left.sender, right.sender);
        break;
      case "event_name":
        compareResult = compareStrings(
          `${left.event_name} ${left.company_name}`,
          `${right.event_name} ${right.company_name}`
        );
        break;
      case "stand_number":
        compareResult = compareStrings(left.stand_number, right.stand_number);
        break;
      case "questions":
        compareResult = left.questions.length - right.questions.length;
        break;
      case "status":
        compareResult = compareStrings(left.status, right.status);
        break;
      case "received_at":
        compareResult = compareDates(left.received_at, right.received_at);
        break;
      case "processed_by":
        compareResult = compareStrings(left.dismissed_by || left.approved_by, right.dismissed_by || right.approved_by);
        break;
      case "processed_at":
        compareResult = compareDates(left.dismissed_at || left.approved_at, right.dismissed_at || right.approved_at);
        break;
      default:
        compareResult = 0;
        break;
    }

    return compareResult * direction;
  });

  return sortedTasks;
};

const getCommunicationRowClassName = (task: TCommunicationTask): string => {
  const classNames = [ "communication-table__row" ];
  const openQuestions = task.questions.filter((question) => question.status === "open").length;

  if (task.status === Communication.Const.statuses.UNIDENTIFIED) {
    classNames.push("communication-table__row--needs-match");
  } else if (task.status === Communication.Const.statuses.DISMISSED) {
    classNames.push("communication-table__row--dismissed");
  } else if (openQuestions > 0) {
    classNames.push("communication-table__row--clarification");
  } else if (task.work_state.status === "in_progress") {
    classNames.push("communication-table__row--in-progress");
  } else if (task.demo_badge_key) {
    classNames.push("communication-table__row--demo");
  }

  if (task.work_state.status === "untouched" && !task.work_state.viewed_at) {
    classNames.push("communication-table__row--unread");
  }

  return classNames.join(" ");
};

const CommunicationsPage: React.FC = () => {
  useActiveMenuItem([ "communication-board" ], [ "communication-board" ]);

  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { me } = useMe("cache-first");
  const canApprove = true;
  const currentTab = getTabFromPath(location.pathname);
  const currentPage = getPageFromPath(location.pathname);
  const viewportWidth = useWindowSize()?.width || 0;
  const communicationMainRef = useRef<HTMLDivElement>(null);
  const previousDocumentTitleRef = useRef<string | null>(null);
  const rowHoverHintRef = useRef<HTMLDivElement>(null);
  const rowHoverHintFrameRef = useRef<number | null>(null);
  const rowHoverHintDelayRef = useRef<number | null>(null);
  const rowHoverHintHideRef = useRef<number | null>(null);
  const rowHoverHintCoordsRef = useRef({ x: 0, y: 0 });
  const [ searchText, setSearchText ] = useState("");
  const [ filters, setFilters ] = useState<TCommunicationFilters>(emptyFilters);
  const [ equipmentOptions, setEquipmentOptions ] = useState<TCommunicationReferenceOption[]>([]);
  const [ mainContentWidth, setMainContentWidth ] = useState(0);
  const [ selectedTaskId, setSelectedTaskId ] = useState<string | null>(null);
  const [ hasUnsavedDrawerChanges, setHasUnsavedDrawerChanges ] = useState(false);
  const [ previewTaskId, setPreviewTaskId ] = useState<string | null>(null);
  const [ pendingScrollTaskId, setPendingScrollTaskId ] = useState<string | null>(null);
  const [ isRowHoverHintVisible, setIsRowHoverHintVisible ] = useState(false);
  const [ sortState, setSortState ] = useState<TCommunicationSortState>({
    key: "received_at",
    order: "descend",
  });
  const currentManagerName = [ me?.name, me?.surname ].filter(Boolean).join(" ").trim()
    || me?.email
    || undefined;
  const isPreviewRailVisible = viewportWidth > PREVIEW_RAIL_BREAKPOINT;
  const showInlineOpenAction = !isPreviewRailVisible;
  const drawerWidth = useMemo(
    () => getCommunicationDrawerWidth(viewportWidth),
    [ viewportWidth ]
  );

  const { tasks, auditEntries, saveTask, approveTask, dismissTask, markAsViewed, markAsInProgress, loading, error } =
    Communication.Helpers.useCommunicationStore(filters, currentManagerName);

  useBreadCrumbs([
    {
      label: localizeByKey("BREADCRUMBS.Label_CommunicationBoard"),
      path: `${basePath}`,
    },
  ]);

  const defaultLang = me?.lang_api || "de";

  const { data: flooringData } = useQuery(GET_FLOORING_CURSOR, {
    variables: { first: 100 },
    fetchPolicy: "cache-first",
  });

  const { data: organizersData } = useQuery(GET_ORGANIZERS_CURSOR, {
    variables: { first: 100 },
    fetchPolicy: "cache-first",
  });

  const { data: exhibitionsData } = useQuery(GET_EXHIBITIONS_CURSOR, {
    variables: { first: 100 },
    fetchPolicy: "cache-first",
  });

  useEffect(() => {
    let isMounted = true;

    const loadEquipmentOptions = async () => {
      if (!defaultLang) {
        if (isMounted) {
          setEquipmentOptions([]);
        }
        return;
      }

      const optionMap = new Map<string, TCommunicationReferenceOption>();
      let page = 1;
      let lastPage = 1;

      try {
        while (page <= lastPage) {
          const { data } = await apolloClient.query({
            query: GET_EQUIPMENTS,
            variables: {
              first: EQUIPMENT_PAGE_SIZE,
              page,
              where: {
                column: "LANG",
                operator: "EQ",
                value: defaultLang,
              },
            },
            fetchPolicy: "cache-first",
          });

          mapEquipmentOptions(data?.equipments?.data).forEach((option) => {
            if (!optionMap.has(option.value)) {
              optionMap.set(option.value, option);
            }
          });

          lastPage = data?.equipments?.paginatorInfo?.lastPage || page;
          page += 1;
        }
      } finally {
        if (isMounted) {
          setEquipmentOptions(sortReferenceOptions(Array.from(optionMap.values())));
        }
      }
    };

    void loadEquipmentOptions();

    return () => {
      isMounted = false;
    };
  }, [ apolloClient, defaultLang ]);

  useEffect(() => {
    const syncMainContentWidth = () => {
      const nextWidth = Math.round(
        communicationMainRef.current?.getBoundingClientRect().width || 0
      );

      setMainContentWidth((currentWidth) =>
        currentWidth === nextWidth ? currentWidth : nextWidth
      );
    };

    syncMainContentWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", syncMainContentWidth);

      return () => {
        window.removeEventListener("resize", syncMainContentWidth);
      };
    }

    const resizeObserver = new ResizeObserver(() => {
      syncMainContentWidth();
    });

    if (communicationMainRef.current) {
      resizeObserver.observe(communicationMainRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const flooringOptions = useMemo<TCommunicationReferenceOption[]>(
    () => mapCursorOptions(flooringData?.flooringsCursor?.edges),
    [ flooringData ]
  );

  const organizerOptions = useMemo<TCommunicationReferenceOption[]>(
    () => mapCursorOptions(organizersData?.organizersCursor?.edges),
    [ organizersData ]
  );

  const exhibitionOptions = useMemo<TCommunicationReferenceOption[]>(
    () => mapCursorOptions(exhibitionsData?.exhibitionTypesCursor?.edges),
    [ exhibitionsData ]
  );

  const summary = useMemo(
    () => Communication.Helpers.getCommunicationSummary(tasks),
    [ tasks ]
  );

  useEffect(() => {
    previousDocumentTitleRef.current = document.title;

    return () => {
      if (previousDocumentTitleRef.current) {
        document.title = previousDocumentTitleRef.current;
      }
    };
  }, []);

  useEffect(() => {
    const inboxCount = summary.queueTotals.inbox;
    const base = localizeByKeyStatic("COMMUNICATION.PageTitle");
    document.title = inboxCount > 0 ? `(${inboxCount}) ${base}` : base;
  }, [ summary.queueTotals.inbox ]);

  const tasksByTab = useMemo<Record<TCommunicationTab, TCommunicationTask[]>>(
    () => ({
      inbox: tasks.filter(
        (task) => task.status !== Communication.Const.statuses.APPROVED
          && task.status !== Communication.Const.statuses.DISMISSED
      ),
      pending: tasks.filter(
        (task) => task.status === Communication.Const.statuses.PENDING
      ),
      unidentified: tasks.filter(
        (task) => task.status === Communication.Const.statuses.UNIDENTIFIED
      ),
      audit: tasks.filter(
        (task) =>
          task.status === Communication.Const.statuses.APPROVED
          || task.status === Communication.Const.statuses.DISMISSED
      ),
    }),
    [ tasks ]
  );

  const visibleTasks = useMemo(
    () => tasksByTab[ currentTab ].filter((task) => matchesSearch(task, searchText)),
    [ currentTab, searchText, tasksByTab ]
  );

  const sortedTasks = useMemo(
    () => sortCommunicationTasks(visibleTasks, sortState),
    [ sortState, visibleTasks ]
  );

  const totalPages = Math.max(1, Math.ceil(sortedTasks.length / Communication.Const.pageSize));
  const safePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    if (safePage !== currentPage) {
      navigate(getRouteByTab(currentTab, safePage), { replace: true });
    }
  }, [ currentPage, currentTab, navigate, safePage ]);

  const paginatedTasks = useMemo(
    () =>
      sortedTasks.slice(
        (safePage - 1) * Communication.Const.pageSize,
        safePage * Communication.Const.pageSize
      ),
    [ safePage, sortedTasks ]
  );

  const auditEntryMap = useMemo<Record<string, TCommunicationAuditEntry | undefined>>(
    () =>
      auditEntries.reduce<Record<string, TCommunicationAuditEntry>>((acc, entry) => {
        if (!acc[ entry.task_id ]) {
          acc[ entry.task_id ] = entry;
        }
        return acc;
      }, {}),
    [ auditEntries ]
  );
  const tableLayoutMode = useMemo(
    () => getTableLayoutMode(mainContentWidth),
    [ mainContentWidth ]
  );
  const tableScrollWidth = useMemo(
    () => getTableScrollWidth(tableLayoutMode),
    [ tableLayoutMode ]
  );

  function handleOpenTask(task: TCommunicationTask) {
    handleRowHoverEnd();
    markAsViewed(task.id);
    setPreviewTaskId(isPreviewRailVisible ? task.id : null);
    setSelectedTaskId(task.id);
    setPendingScrollTaskId(task.id);
  }

  const handleRouteToAuditAfterProcessing = (taskId: string) => {
    setHasUnsavedDrawerChanges(false);
    setSelectedTaskId(null);
    setPreviewTaskId(isPreviewRailVisible ? taskId : null);
    setPendingScrollTaskId(taskId);
    navigate(getRouteByTab("audit"));
  };

  const handleApproveTask = (taskId: string, values: TCommunicationTaskFormValues) => {
    approveTask(taskId, values);
    handleRouteToAuditAfterProcessing(taskId);
  };

  const handleDismissTask = (taskId: string, reason: TCommunicationDismissReason) => {
    dismissTask(taskId, reason);
    handleRouteToAuditAfterProcessing(taskId);
  };

  const rawColumns = useMemo<ColumnsType<TCommunicationTask>>(
    () =>
      Communication.Helpers.createCommunicationColumns({
        currentTab,
        layoutMode: tableLayoutMode,
        showInlineOpenAction,
        canEditRaw: false,
        canApprove,
        equipmentOptions,
        flooringOptions,
        organizerOptions,
        exhibitionOptions,
        auditEntryMap,
        onOpenTask: handleOpenTask,
        onSave: (taskId, values) => saveTask(taskId, values),
        onApprove: handleApproveTask,
      }),
    [
      auditEntryMap,
      currentTab,
      tableLayoutMode,
      showInlineOpenAction,
      equipmentOptions,
      exhibitionOptions,
      flooringOptions,
      organizerOptions,
      canApprove,
      handleOpenTask,
      handleApproveTask,
      saveTask,
    ]
  );

  const columns = useMemo(
    () =>
      rawColumns.map((column) =>
        column.sorter
          ? {
            ...column,
            sortOrder:
              sortState.key === String(column.key)
                ? sortState.order
                : undefined,
          }
          : column
      ),
    [ rawColumns, sortState ]
  );
  const selectedTask = useMemo(
    () => tasks.find((task) => task.id === selectedTaskId) || null,
    [ selectedTaskId, tasks ]
  );

  const previewTask = useMemo(
    () => tasks.find((task) => task.id === previewTaskId) || null,
    [ previewTaskId, tasks ]
  );

  const activeFilterCount = useMemo(() => getActiveFilterCount(filters), [ filters ]);
  const hasActiveFilters = activeFilterCount > 0;
  const historyCount = tasksByTab.audit.length;
  const backlogCount = tasksByTab[ currentTab ].length;
  const pageNavbar = [
    {
      label: renderTabLabel({
        label: localizeByKey("NAVBAR_PAGES.Label_CommunicationInbox"),
        count: summary.queueTotals.inbox,
        tooltip: localizeByKey("COMMUNICATION.TabTooltip_Inbox"),
      }),
      path: "communication-board",
    },
    {
      label: renderTabLabel({
        label: localizeByKey("NAVBAR_PAGES.Label_CommunicationPending"),
        count: summary.queueTotals.pending,
        tooltip: localizeByKey("COMMUNICATION.TabTooltip_Pending"),
      }),
      path: "communication-board/pending",
    },
    {
      label: renderTabLabel({
        label: localizeByKey("NAVBAR_PAGES.Label_CommunicationUnidentified"),
        count: summary.queueTotals.unidentified,
        tooltip: localizeByKey("COMMUNICATION.TabTooltip_Unidentified"),
      }),
      path: "communication-board/unidentified",
    },
    {
      label: renderTabLabel({
        label: localizeByKey("NAVBAR_PAGES.Label_CommunicationAudit"),
        count: currentTab === "audit" ? historyCount : undefined,
        tooltip: localizeByKey("COMMUNICATION.TabTooltip_Audit"),
      }),
      path: "communication-board/audit",
    },
  ];

  const summaryCards = [
    {
      key: "inbox",
      label: localizeByKey("COMMUNICATION.Summary_Inbox"),
      value: summary.queueTotals.inbox,
    },
    {
      key: "unidentified",
      label: localizeByKey("COMMUNICATION.Summary_Unidentified"),
      value: summary.queueTotals.unidentified,
    },
    {
      key: "pending",
      label: localizeByKey("COMMUNICATION.Summary_Pending"),
      value: summary.queueTotals.pending,
    },
    {
      key: "parser-processed",
      label: localizeByKey("COMMUNICATION.Summary_ParserProcessed"),
      value: summary.queueTotals.parserProcessedToday,
    },
    {
      key: "manager-approved",
      label: localizeByKey("COMMUNICATION.Summary_ManagerApproved"),
      value: summary.queueTotals.managerApprovedToday,
    },
  ];

  const overallOpenClientSummary = useMemo(() => {
    const openTasks = tasks.filter(
      (task) =>
        task.status !== Communication.Const.statuses.APPROVED
        && task.status !== Communication.Const.statuses.DISMISSED
    );
    const companyFallback = localizeByKeyStatic("COMMUNICATION.TopClients_Unassigned");
    const summaryMap = new Map<string, {
      companyName: string;
      emailCount: number;
      openQuestionsCount: number;
      needMatchCount: number;
      reviewCount: number;
    }>();

    openTasks.forEach((task) => {
      const companyName = task.company_name.trim() || companyFallback;
      const current = summaryMap.get(companyName) || {
        companyName,
        emailCount: 0,
        openQuestionsCount: 0,
        needMatchCount: 0,
        reviewCount: 0,
      };

      current.emailCount += 1;
      current.openQuestionsCount += task.questions.filter(
        (question) => question.status === "open"
      ).length;

      if (task.status === Communication.Const.statuses.UNIDENTIFIED) {
        current.needMatchCount += 1;
      }

      if (task.status === Communication.Const.statuses.PENDING) {
        current.reviewCount += 1;
      }

      summaryMap.set(companyName, current);
    });

    const sortedItems = Array.from(summaryMap.values())
      .filter((item) =>
        item.needMatchCount > 0
        || item.reviewCount > 0
        || item.openQuestionsCount > 0
      )
      .sort((left, right) =>
        (right.needMatchCount + right.reviewCount + right.openQuestionsCount)
          - (left.needMatchCount + left.reviewCount + left.openQuestionsCount)
        || right.emailCount - left.emailCount
        || right.openQuestionsCount - left.openQuestionsCount
        || left.companyName.localeCompare(right.companyName)
      );

    return {
      items: sortedItems.slice(0, 5),
      hiddenCount: Math.max(0, sortedItems.length - 5),
    };
  }, [ tasks ]);

  const queueHelpKeyMap: Record<TCommunicationTab, string> = {
    inbox: "COMMUNICATION.Help_Inbox",
    pending: "COMMUNICATION.Help_Pending",
    unidentified: "COMMUNICATION.Help_Unidentified",
    audit: "COMMUNICATION.Help_Audit",
  };

  const emptyKeyMap: Record<TCommunicationTab, string> = {
    inbox: "COMMUNICATION.Empty_Inbox",
    pending: "COMMUNICATION.Empty_Pending",
    unidentified: "COMMUNICATION.Empty_Unidentified",
    audit: "COMMUNICATION.Empty_Audit",
  };

  const resultsKey =
    currentTab === "audit" ? "COMMUNICATION.Results_Audit" : "COMMUNICATION.Results_Inbox";
  const isDrawerOpen = Boolean(selectedTaskId && selectedTask);
  const activeTableTaskId = isDrawerOpen
    ? selectedTaskId
    : isPreviewRailVisible
      ? previewTaskId
      : null;

  const confirmDiscardUnsavedChanges = () => {
    if (!hasUnsavedDrawerChanges) {
      return true;
    }

    return window.confirm(
      localizeByKeyStatic("COMMUNICATION.Warning_UnsavedChangesConfirm")
    );
  };

  const handleCloseTaskDrawer = (options?: { ignoreUnsavedChanges?: boolean }) => {
    if (!options?.ignoreUnsavedChanges && !confirmDiscardUnsavedChanges()) {
      return;
    }

    setHasUnsavedDrawerChanges(false);
    setSelectedTaskId(null);
  };

  const handleCloseTaskDrawerFromUi = () => {
    handleCloseTaskDrawer();
  };

  const selectedTaskIndex = useMemo(
    () => sortedTasks.findIndex((t) => t.id === selectedTaskId),
    [ sortedTasks, selectedTaskId ]
  );
  const hasPrevTask = selectedTaskIndex > 0;
  const hasNextTask = selectedTaskIndex < sortedTasks.length - 1;

  const handleNavigateTask = (direction: "prev" | "next") => {
    if (!confirmDiscardUnsavedChanges()) {
      return;
    }

    const nextIndex = direction === "prev" ? selectedTaskIndex - 1 : selectedTaskIndex + 1;
    const nextTask = sortedTasks[nextIndex];
    if (nextTask) {
      const nextPage = Math.floor(nextIndex / Communication.Const.pageSize) + 1;
      setHasUnsavedDrawerChanges(false);
      setPreviewTaskId(isPreviewRailVisible ? nextTask.id : null);
      setSelectedTaskId(nextTask.id);
      setPendingScrollTaskId(nextTask.id);

      if (nextPage !== safePage) {
        navigate(getRouteByTab(currentTab, nextPage));
      }
    }
  };

  const handlePreviewTask = (task: TCommunicationTask) => {
    if (!isPreviewRailVisible) {
      return;
    }

    setPreviewTaskId((prev) => (prev === task.id ? null : task.id));
  };

  const handleClosePreview = () => {
    setPreviewTaskId(null);
  };

  const updateRowHoverHintPosition = (clientX: number, clientY: number) => {
    if (!rowHoverHintRef.current || typeof window === "undefined") {
      return;
    }

    const nextX = Math.min(
      clientX + ROW_HINT_OFFSET_X,
      window.innerWidth - ROW_HINT_WIDTH - 12
    );
    const nextY = Math.min(
      clientY + ROW_HINT_OFFSET_Y,
      window.innerHeight - ROW_HINT_HEIGHT - 12
    );

    rowHoverHintRef.current.style.transform = `translate3d(${Math.max(12, nextX)}px, ${Math.max(12, nextY)}px, 0)`;
  };

  const clearRowHoverHintTimers = () => {
    if (rowHoverHintDelayRef.current !== null) {
      clearTimeout(rowHoverHintDelayRef.current);
      rowHoverHintDelayRef.current = null;
    }

    if (rowHoverHintHideRef.current !== null) {
      clearTimeout(rowHoverHintHideRef.current);
      rowHoverHintHideRef.current = null;
    }
  };

  const scheduleRowHoverHint = () => {
    clearRowHoverHintTimers();

    rowHoverHintDelayRef.current = window.setTimeout(() => {
      updateRowHoverHintPosition(
        rowHoverHintCoordsRef.current.x,
        rowHoverHintCoordsRef.current.y
      );
      setIsRowHoverHintVisible(true);
      rowHoverHintDelayRef.current = null;

      rowHoverHintHideRef.current = window.setTimeout(() => {
        setIsRowHoverHintVisible(false);
        rowHoverHintHideRef.current = null;
      }, ROW_HINT_HIDE_MS);
    }, ROW_HINT_DELAY_MS);
  };

  const isRowHintSuppressedTarget = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) {
      return true;
    }

    return target.tagName !== "TD" && !target.classList.contains("ant-table-cell");
  };

  const handleRowHoverStart = (event: React.MouseEvent<HTMLElement>) => {
    if (isRowHintSuppressedTarget(event.target)) {
      handleRowHoverEnd();
      return;
    }

    rowHoverHintCoordsRef.current = {
      x: event.clientX,
      y: event.clientY,
    };

    scheduleRowHoverHint();
  };

  const handleRowHoverMove = (event: React.MouseEvent<HTMLElement>) => {
    if (isRowHintSuppressedTarget(event.target)) {
      handleRowHoverEnd();
      return;
    }

    rowHoverHintCoordsRef.current = {
      x: event.clientX,
      y: event.clientY,
    };

    if (rowHoverHintFrameRef.current !== null) {
      cancelAnimationFrame(rowHoverHintFrameRef.current);
      rowHoverHintFrameRef.current = null;
    }

    if (isRowHoverHintVisible) {
      setIsRowHoverHintVisible(false);
    }

    scheduleRowHoverHint();
  };

  const handleRowHoverEnd = () => {
    clearRowHoverHintTimers();

    if (rowHoverHintFrameRef.current !== null) {
      cancelAnimationFrame(rowHoverHintFrameRef.current);
      rowHoverHintFrameRef.current = null;
    }

    setIsRowHoverHintVisible(false);
  };

  useEffect(() => () => {
    clearRowHoverHintTimers();

    if (rowHoverHintFrameRef.current !== null) {
      cancelAnimationFrame(rowHoverHintFrameRef.current);
    }
  }, []);

  useEffect(() => {
    if (!isDrawerOpen || !hasUnsavedDrawerChanges) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [ hasUnsavedDrawerChanges, isDrawerOpen ]);

  useEffect(() => {
    if (!isPreviewRailVisible || !isDrawerOpen || !selectedTaskId || previewTaskId === selectedTaskId) {
      return;
    }

    setPreviewTaskId(selectedTaskId);
  }, [ isDrawerOpen, isPreviewRailVisible, previewTaskId, selectedTaskId ]);

  useEffect(() => {
    if (isPreviewRailVisible || previewTaskId === null) {
      return;
    }

    setPreviewTaskId(null);
  }, [ isPreviewRailVisible, previewTaskId ]);

  useEffect(() => {
    if (!pendingScrollTaskId) {
      return;
    }

    const scrollToSelectedRow = () => {
      const row = communicationMainRef.current?.querySelector(
        `.communication-table tbody tr[data-row-key="${pendingScrollTaskId}"]`
      ) as HTMLElement | null;

      if (!row) {
        return false;
      }

      row.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });

      setPendingScrollTaskId(null);
      return true;
    };

    const frameId = window.requestAnimationFrame(() => {
      if (!scrollToSelectedRow()) {
        window.setTimeout(scrollToSelectedRow, 80);
      }
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [ paginatedTasks, pendingScrollTaskId, safePage ]);

  return (
    <PageWrapDefault
      className="page-communication"
      dataExist={true}
      title={<Localize>PAGES.Title_CommunicationBoard</Localize>}
      pageNavbar={pageNavbar}
      staticPath=""
    >
      <div className="communication-layout">
        <div ref={communicationMainRef} className="communication-main">
          <div className="table-action-bar">
            <div className="col">
              <Search
                className="filter-search"
                prefix={<Icons.Search />}
                value={searchText}
                placeholder={localizeByKey("COMMUNICATION.Placeholder_Search")}
                onChange={(event) => setSearchText(event.target.value)}
              />

              <div className="clear-css-prop table-filter-form">
                <DrawerStandart
                  extraClass="modal-form filters"
                  modalButton={
                    <Button icon={<Icons.Filter />}>
                      <span className="ellipsis">
                        <Localize>FORMS.Button_Text_Filters</Localize>
                      </span>
                      {!!activeFilterCount && (
                        <span className="filter-count">{activeFilterCount}</span>
                      )}
                    </Button>
                  }
                >
                  <strong className="modal-title">
                    <span className="text">
                      <Localize>COMMUNICATION.Title_Filters</Localize>
                    </span>
                  </strong>

                  <Communication.Forms.Filters
                    filters={filters}
                    onApply={(values) => setFilters(values)}
                    onReset={() => setFilters(emptyFilters)}
                  />
                </DrawerStandart>

                {hasActiveFilters && (
                  <Button className="underline" onClick={() => setFilters(emptyFilters)}>
                    <span>
                      <Localize>FORMS.Button_Text_Reset</Localize>
                    </span>
                  </Button>
                )}
              </div>
            </div>

            <div className="col">
              <div className="communication-access-note communication-access-note--readonly">
                <Icons.Information />
                <span>
                  <Localize>COMMUNICATION.PreviewMode_PageNote</Localize>
                </span>
              </div>
            </div>
          </div>

          {error && (
            <Alert
              showIcon
              type="warning"
              message={error}
              style={{ marginBottom: 16 }}
            />
          )}

          <div className="communication-results-line">
            <span className="communication-results-line__summary">
              <Localize total={backlogCount} visible={sortedTasks.length}>
                {resultsKey}
              </Localize>
            </span>
          </div>

          <Table<TCommunicationTask>
            rowKey="id"
            loading={loading}
            size={tableLayoutMode === "full" ? "middle" : "small"}
            className={`table-main communication-table communication-table--${tableLayoutMode}${showInlineOpenAction ? " communication-table--inline-open" : ""}`}
            rowClassName={(task) => {
              const base = getCommunicationRowClassName(task);
              return activeTableTaskId === task.id ? `${base} communication-table__row--selected` : base;
            }}
            tableLayout="fixed"
            columns={columns}
            dataSource={paginatedTasks}
            scroll={{ x: tableScrollWidth }}
            onRow={(task) => ({
              onClick: (event) => {
                if (isInteractiveRowTarget(event.target)) {
                  return;
                }

                if (!isPreviewRailVisible) {
                  return;
                }

                handlePreviewTask(task);
              },
              onMouseEnter: isPreviewRailVisible ? handleRowHoverStart : undefined,
              onMouseMove: isPreviewRailVisible ? handleRowHoverMove : undefined,
              onMouseLeave: isPreviewRailVisible ? handleRowHoverEnd : undefined,
              "aria-label": isPreviewRailVisible
                ? localizeByKeyStatic("COMMUNICATION.Hint_RowClickWorkflow")
                : undefined,
            })}
            pagination={{
              className: "main-table-pagination",
              current: safePage,
              pageSize: Communication.Const.pageSize,
              total: sortedTasks.length,
              showSizeChanger: false,
              size: tableLayoutMode === "tablet" ? "small" : undefined,
              position: [ "bottomLeft" ],
              hideOnSinglePage: true,
            }}
            locale={{ emptyText: localizeByKey(emptyKeyMap[ currentTab ]) }}
            onChange={(pagination, _tableFilters, sorter) => {
              const nextSorter = (Array.isArray(sorter) ? sorter[ 0 ] : sorter) as
                | SorterResult<TCommunicationTask>
                | undefined;

              setSortState({
                key: nextSorter?.columnKey ? String(nextSorter.columnKey) : undefined,
                order: nextSorter?.order || undefined,
              });

              navigate(getRouteByTab(currentTab, pagination.current || 1));
            }}
          />

          {isPreviewRailVisible && (
            <div
              ref={rowHoverHintRef}
              className={`communication-row-cursor-hint${isRowHoverHintVisible ? " communication-row-cursor-hint--visible" : ""}`}
            >
              <Localize>COMMUNICATION.Hint_RowClickWorkflow</Localize>
            </div>
          )}
        </div>

        <aside className={`communication-sidebar${isPreviewRailVisible && previewTask ? " communication-sidebar--preview" : ""}`}>
          {isPreviewRailVisible && previewTask ? (
            <Communication.Fields.ReadingPane
              task={previewTask}
              currentTab={currentTab}
              onOpen={() => handleOpenTask(previewTask)}
              onClose={handleClosePreview}
            />
          ) : (
          <>
          <div className="communication-help-block">
            <strong><Localize>{queueHelpKeyMap[ currentTab ]}</Localize></strong>
          </div>

          <div className="communication-summary-strip">
            {summaryCards.map((card) => (
              <div key={card.key} className="communication-summary-strip__item">
                <span className="communication-summary-strip__label">{card.label}</span>
                <strong className="communication-summary-strip__value">{card.value}</strong>
              </div>
            ))}
          </div>

          <div className="communication-top-clients">
            <div className="communication-top-clients__header">
              <h3>
                <Localize>COMMUNICATION.Title_OverallClientSummary</Localize>
              </h3>
            </div>

            {overallOpenClientSummary.items.length ? (
              <div className="communication-top-clients__list">
                {overallOpenClientSummary.items.map((item) => (
                  <div
                    key={item.companyName}
                    className="communication-top-clients__row"
                  >
                    <div className="communication-top-clients__copy">
                      <span>{item.companyName}</span>
                      <small>
                        {[
                          item.needMatchCount > 0
                            ? localizeByKeyStatic("COMMUNICATION.TopClients_NeedMatch", {
                              count: item.needMatchCount,
                            })
                            : "",
                          item.reviewCount > 0
                            ? localizeByKeyStatic("COMMUNICATION.TopClients_ToReview", {
                              count: item.reviewCount,
                            })
                            : "",
                          item.openQuestionsCount > 0
                            ? localizeByKeyStatic("COMMUNICATION.TopClients_OpenQuestions", {
                              open: item.openQuestionsCount,
                            })
                            : "",
                        ].filter(Boolean).join(" / ")}
                      </small>
                    </div>

                    <strong className="communication-top-clients__value">
                      <Localize total={item.emailCount}>
                        {item.emailCount === 1
                          ? "COMMUNICATION.TopClients_EmailCount"
                          : "COMMUNICATION.TopClients_EmailCountPlural"}
                      </Localize>
                    </strong>
                  </div>
                ))}

                {overallOpenClientSummary.hiddenCount > 0 && (
                  <div className="communication-top-clients__more">
                    <Localize count={overallOpenClientSummary.hiddenCount}>
                      COMMUNICATION.TopClients_MoreAttention
                    </Localize>
                  </div>
                )}
              </div>
            ) : (
              <div className="communication-top-clients__empty">
                <Localize>COMMUNICATION.TopClients_EmptyAttention</Localize>
              </div>
            )}
          </div>
          </>
          )}
        </aside>
      </div>

      <Drawer
        className="drawer-standard model-form communication-drawer"
        open={isDrawerOpen}
        width={drawerWidth}
        placement="right"
        closable={false}
        onClose={handleCloseTaskDrawerFromUi}
        title={null}
        footer={null}
      >
        <ConfigProvider theme={customizeTheme.LightTheme()}>
          {selectedTask && (
            <>
              <div className="communication-drawer__header-row">
                <strong className="modal-title">
                  <span className="text">
                    <Localize>{getDrawerTitleKey(currentTab, selectedTask)}</Localize>
                  </span>
                </strong>
                <div className="communication-drawer__header-actions">
                  <div className="communication-drawer__nav">
                    <button
                      type="button"
                      className="communication-drawer__nav-btn"
                      disabled={!hasPrevTask}
                      onClick={() => handleNavigateTask("prev")}
                      title="Previous"
                    >
                      <LeftOutlined />
                    </button>
                    <span className="communication-drawer__nav-counter">
                      {selectedTaskIndex + 1} / {sortedTasks.length}
                    </span>
                    <button
                      type="button"
                      className="communication-drawer__nav-btn"
                      disabled={!hasNextTask}
                      onClick={() => handleNavigateTask("next")}
                      title="Next"
                    >
                      <RightOutlined />
                    </button>
                  </div>

                  <button
                    type="button"
                    className="communication-drawer__close-btn"
                    onClick={handleCloseTaskDrawerFromUi}
                    title="Close"
                    aria-label="Close"
                  >
                    <Icons.Close />
                  </button>
                </div>
              </div>

              <Communication.Forms.Task
                task={selectedTask}
                canEditRaw={false}
                canApprove={currentTab !== "audit"}
                isAuditView={
                  currentTab === "audit"
                  || selectedTask.status === Communication.Const.statuses.APPROVED
                  || selectedTask.status === Communication.Const.statuses.DISMISSED
                }
                equipmentOptions={equipmentOptions}
                flooringOptions={flooringOptions}
                organizerOptions={organizerOptions}
                exhibitionOptions={exhibitionOptions}
                auditEntry={auditEntryMap[ selectedTask.id ]}
                onSave={(taskId, values) => saveTask(taskId, values)}
                onApprove={handleApproveTask}
                onDismissNotRelevant={handleDismissTask}
                onDirtyStart={(taskId) => markAsInProgress(taskId)}
                onDirtyChange={setHasUnsavedDrawerChanges}
                action={handleCloseTaskDrawer}
                showModal={true}
              />
            </>
          )}
        </ConfigProvider>
      </Drawer>
    </PageWrapDefault>
  );
};

export default CommunicationsPage;
